if (!customElements.get('load-more')) {
  customElements.define(
    'load-more',
    class LoadMore extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.loadMoreButton = this.querySelector('.js-load-more');
        this.loadMoreDisabledButton = this.querySelector('.js-load-more-disabled');
        this.paginationWrapper = this.closest('.pagination-wrapper');
        this.pagination = document.querySelector('.pagination');
        this.containerWrapperSelector = this.paginationWrapper.closest('[data-container-wrapper]');
        this.containerSelector = this.containerWrapperSelector.querySelector('[data-container]');

        const urlParams = new URLSearchParams(window.location.search);
        this.currentPage = parseInt(urlParams.get('page')) || 1;

        this.totalPages = this.extractTotalPages();

        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', () => this.loadMoreProducts());
        }
      }

      parsePageFromUrl(urlString) {
        try {
          const u = new URL(urlString, window.location.href);
          const p = parseInt(u.searchParams.get('page'), 10);
          return Number.isFinite(p) && p >= 1 ? p : null;
        } catch {
          return null;
        }
      }

      loadMoreProducts() {
        const nextPageUrl = this.getNextPageUrl();
        if (!nextPageUrl) return;
        if (this._loadMoreInFlight) return;
        this._loadMoreInFlight = true;
        this.loadMoreButton?.setAttribute('disabled', 'disabled');

        const pageFromNextUrl = this.parsePageFromUrl(nextPageUrl);

        fetch(nextPageUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.text();
          })
          .then((responseHTML) => {
            const parser = new DOMParser();
            const html = parser.parseFromString(responseHTML, 'text/html');
            const newItems = Array.from(html.querySelector('[data-container]').children);

            newItems.forEach((item) => this.containerSelector.appendChild(item));

            if (pageFromNextUrl != null) {
              this.currentPage = pageFromNextUrl;
            } else {
              this.currentPage += 1;
            }

            const newNextPageUrl = this.getNextPageUrl(html);
            this.updateNextPageState(newNextPageUrl);
            this.updatePaginationElements(html);

            const tp = this.extractTotalPages(this.pagination);
            if (tp != null) {
              this.totalPages = tp;
            }
            if (this.totalPages != null && this.totalPages >= 1) {
              this.currentPage = Math.min(this.currentPage, this.totalPages);
            }

            this.updateCollectionHeading();
            this.updateBrowserUrl();
            this.updateDocumentTitle();
          })
          .catch(() => {})
          .finally(() => {
            this._loadMoreInFlight = false;
            this.loadMoreButton?.removeAttribute('disabled');
          });
      }

      getNextPageUrl(doc = document) {
        const nextLink = doc.querySelector('.js-pagination-next');
        return nextLink ? nextLink.getAttribute('href') : null;
      }

      updatePaginationElements(html) {
        const newPagination = html.querySelector('.pagination');
        if (!newPagination || !this.pagination) return;
        this.pagination.innerHTML = newPagination.innerHTML;
        if (newPagination.dataset.totalPages) {
          this.pagination.dataset.totalPages = newPagination.dataset.totalPages;
        }
        if (newPagination.dataset.currentPage) {
          this.pagination.dataset.currentPage = newPagination.dataset.currentPage;
        }

        const newTotalPages = this.extractTotalPages(newPagination);
        if (newTotalPages != null) {
          this.totalPages = newTotalPages;
        }
      }

      updateNextPageState(nextPageUrl) {
        if (nextPageUrl) {
          this.loadMoreButton.classList.remove('hidden');
          this.loadMoreDisabledButton.classList.add('hidden');
        } else {
          this.loadMoreButton.classList.add('hidden');
          this.loadMoreDisabledButton.classList.remove('hidden');
        }
      }

      updateBrowserUrl() {
        const currentUrl = new URL(window.location.href);

        if (this.currentPage > 1) {
          currentUrl.searchParams.set('page', this.currentPage.toString());
        } else {
          currentUrl.searchParams.delete('page');
        }

        currentUrl.searchParams.delete('phcursor');

        window.history.pushState({ page: this.currentPage }, '', currentUrl.toString());
      }

      updateDocumentTitle() {
        if (this.currentPage <= 1) {
          document.title = document.title.replace(/\s*[–-]\s*Strana\s+\d+(\s+z\s+\d+)?/i, '');
          return;
        }
        const zPart = this.totalPages != null && this.totalPages >= 1 ? ` z ${this.totalPages}` : '';
        const stranaSegment = ` – Strana ${this.currentPage}${zPart}`;
        let t = document.title;
        if (/\s*[–-]\s*Strana\s+\d+/i.test(t)) {
          t = t.replace(/\s*[–-]\s*Strana\s+\d+(\s+z\s+\d+)?/i, stranaSegment);
        } else {
          const en = '–';
          const needle = ` ${en} `;
          const idx = t.lastIndexOf(needle);
          if (idx !== -1) {
            t = t.slice(0, idx) + stranaSegment + t.slice(idx);
          } else {
            t = `${t}${stranaSegment}`;
          }
        }
        document.title = t;
      }

      updateCollectionHeading() {
        // Try multiple selectors to find the heading
        const headingSelectors = [
          '.collection-page-title h1',
          '.collection-page-title',
          'h1.collection-page-title',
          '[class*="collection-page-title"]',
        ];

        let headingElements = [];
        for (const selector of headingSelectors) {
          headingElements = document.querySelectorAll(selector);
          if (headingElements.length > 0) break;
        }

        if (headingElements.length === 0) {
          return;
        }

        const firstHeading = headingElements[0];
        let baseTitle = firstHeading.textContent || firstHeading.innerText || '';

        const originalTitle = baseTitle;

        baseTitle = baseTitle.replace(/\s+/g, ' ').trim();

        baseTitle = baseTitle.replace(/\s*-\s*Strana\s+\d+\s+z\s+\d+/gi, '').trim();

        let newTitle = baseTitle;
        if (this.currentPage > 1) {
          if (this.totalPages) {
            newTitle = `${baseTitle} - Strana ${this.currentPage} z ${this.totalPages}`;
          } else {
            newTitle = `${baseTitle} - Strana ${this.currentPage}`;
          }
        }

        headingElements.forEach((heading) => {
          heading.textContent = newTitle;
        });
      }

      extractTotalPages(paginationContainer = document) {
        const nav =
          paginationContainer?.matches?.('.pagination') === true
            ? paginationContainer
            : paginationContainer.querySelector?.('.pagination');
        const fromData = nav?.dataset?.totalPages;
        if (fromData != null && fromData !== '') {
          const n = parseInt(fromData, 10);
          if (Number.isFinite(n) && n >= 1) {
            return n;
          }
        }

        const paginationInfo = paginationContainer.querySelector('.pagination__summary');
        if (paginationInfo) {
          const match = paginationInfo.textContent.match(/z (\d+)/);
          if (match) {
            return parseInt(match[1]);
          }
        }

        const infoElements = paginationContainer.querySelectorAll(
          '.pagination__info, .pagination-info, [class*="pagination"][class*="info"]',
        );
        for (const element of infoElements) {
          const match = element.textContent.match(/z (\d+)|of (\d+)/i);
          if (match) {
            const total = match[1] || match[2];
            return parseInt(total);
          }
        }

        const pageLinks = paginationContainer.querySelectorAll('.pagination__item[href]');
        let maxPage = 1;
        pageLinks.forEach((link) => {
          const href = link.getAttribute('href');
          const match = href.match(/[?&]page=(\d+)/);
          if (match) {
            maxPage = Math.max(maxPage, parseInt(match[1]));
          }
        });

        if (maxPage > 1) {
          return maxPage;
        }

        const lastLink = paginationContainer.querySelector('.pagination__item--last[href]');
        if (lastLink) {
          const match = lastLink.getAttribute('href').match(/[?&]page=(\d+)/);
          if (match) {
            return parseInt(match[1]);
          }
        }

        return null;
      }
    },
  );
}
